import React, { useMemo, useState, useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";

import PageLayout from "../_layout/PageLayout";
import DataGrid from "../../components/datatable/DataGrid";
import { SectionMenuItemsModel } from "../sewer-objects/section-details/SectionMenuItems";
import DeleteButton from "../../components/buttons/delete-button/DeleteButton";
import {
  ActionsSection,
  ButtonsSection,
} from "../../components/data-components/sections/DataSections";
import { DamagesStorage, DamagesStorageModel } from "../../services/damage/DamagesStorage";
import { CurrentSectionStorageModel } from "../../services/sewer-object/CurrentSectionStorage";
import { snackbarNotificationsService } from "../../services/notifications/SnackbarNotificationsService";
import {
  DeleteDamage,
  CreateDamage,
  ViewDamagesFromAssignedProject,
  ViewDamagesList,
  ManageDeletedData,
  CreateDamageInAssignedProject,
  DeleteDamageFromAssignedProject,
} from "../../services/authorization/Permissions";
import useErrorHandler, {
  ApplicationError,
} from "../../services/error-handler/ErrorHandlerService";
import { api } from "../../services/api/ApiService";
import { permissionsService } from "../../services/authorization/UserPermissionsService";
import { CurrentInspectionStorageModel } from "../../services/inspection/CurrentInspectionStorage";
import useUrlParams from "../../services/url/UrlParamsService";
import { AddNewDamageButton } from "./components/buttons/AddNewDamageButton";
import { useHistory } from "react-router-dom";
import { EntityTypeFromPath } from "../../services/breadcrumbs/EntityTypeFromPathModel";
import useRouteService from "../../services/route-service/RouteService";
import { breadcrumbsStorage } from "../../services/breadcrumbs/BreadcrumbsStorage";
import PipeDiagram from "../../components/inspection/PipeDiagram";
import { Grid } from "@mui/material";
import { featureFlagsStorage } from "../../services/feature-flags/FeatureFlagsStorage";
import { Flags } from "../../generated/FeatureFlags";

const DamagesPage = observer(() => {
  const showPipeDiagram = featureFlagsStorage.isFeatureEnabled(Flags.Show_Pipe_Graph);
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { isUserOnProjects } = useRouteService();

  const [currentSectionStorage] = useState(() =>
    CurrentSectionStorageModel.create(
      {
        currentSectionId: urlParamsService.getSectionId()!,
        parentEntityFromPath: {
          id: urlParamsService.getSewerObjectParentId()!,
          type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job,
        },
      },
      { api }
    )
  );

  const { handleErrorInSnackbar } = useErrorHandler();

  const currentInspectionId = urlParamsService.getInspectionId()!;
  const [currentInspectionStorage] = useState(() =>
    CurrentInspectionStorageModel.create({ currentInspectionId }, { api })
  );

  const [damagesStorage, setDamagesStorage] = useState<DamagesStorage>();

  useEffect(() => {
    if (currentInspectionStorage.isLoading) {
      return;
    }

    setDamagesStorage(
      DamagesStorageModel.create(
        {
          inspectionId: currentInspectionId,
          hasIpfFile: currentInspectionStorage.currentInspection.hasIpfFile,
          useGridFiltering: true,
          autoLoad: true,
          sortByStation: true
        },
        { api }
      )
    );
  }, [
    currentInspectionId,
    currentInspectionStorage.currentInspection.hasIpfFile,
    currentInspectionStorage.isLoading,
  ]);

  const menuItems = useMemo(() => {
    const sectionMenuItems = SectionMenuItemsModel.create({
      sectionId: currentSectionStorage.currentSectionId,
      section: currentSectionStorage.currentSection,
      parentIdFromPath: currentSectionStorage.parentEntityFromPath.id,
    });

    return {
      staticItems: sectionMenuItems.getStaticMenuItems(),
      dynamicItems: sectionMenuItems.getDynamicMenuItems(),
    };
  }, [
    currentSectionStorage.currentSectionId,
    currentSectionStorage.currentSection,
    currentSectionStorage.parentEntityFromPath.id,
  ]);

  const isPageLoading = useMemo(
    () => !currentSectionStorage.currentSection || currentInspectionStorage.isLoading,
    [currentInspectionStorage.isLoading, currentSectionStorage.currentSection]
  );

  const getDamagesAreLockedWarning = useCallback(() => {
    if (
      currentInspectionStorage.inspectionIsLockedBySomeoneElse &&
      currentInspectionStorage.userWithLockOnInspection
    )
      return {
        key: "DamagesPage.locked-inspection",
        context: { userName: currentInspectionStorage.userWithLockOnInspection.name },
      };
    else return undefined;
  }, [
    currentInspectionStorage.inspectionIsLockedBySomeoneElse,
    currentInspectionStorage.userWithLockOnInspection,
  ]);

  const getCurrentInspectionName = useCallback(
    () =>
      currentInspectionStorage.currentInspection.getName(
        currentSectionStorage.currentSection.inspectionsWithDates
      ),
    [
      currentInspectionStorage.currentInspection,
      currentSectionStorage.currentSection.inspectionsWithDates,
    ]
  );

  const isDeleteDamageDisabled = useCallback(
    () =>
      currentInspectionStorage.inspectionIsLockedBySomeoneElse ||
      !permissionsService.userHasOneOfThePermissions([
        DeleteDamage,
        DeleteDamageFromAssignedProject,
      ]) ||
      !damagesStorage?.anyItemsSelected,
    [currentInspectionStorage.inspectionIsLockedBySomeoneElse, damagesStorage?.anyItemsSelected]
  );

  const isCreateDamageDisabled = useMemo(
    () =>
      currentInspectionStorage.inspectionIsLockedBySomeoneElse ||
      !permissionsService.userHasOneOfThePermissions([CreateDamage, CreateDamageInAssignedProject]),
    [currentInspectionStorage.inspectionIsLockedBySomeoneElse]
  );

  const deleteSelectedDamages = useCallback(async () => {
    if (damagesStorage) {
      const selectedDamagesCount = damagesStorage?.selectedItemsCount;
      try {
        await damagesStorage.deleteDamages(damagesStorage?.selectedItems);
        snackbarNotificationsService.showSuccess("DamagesPage.delete.success", {
          itemsCount: selectedDamagesCount!,
        });
      } catch (error) {
        const errorContext = { itemsCount: selectedDamagesCount };
        const applicationError = error as ApplicationError;
        applicationError.withContext(errorContext);
        applicationError.withErrorMessage("default-error.damages.delete");
        handleErrorInSnackbar(applicationError);
      }
    }
  }, [damagesStorage, handleErrorInSnackbar]);

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([
        {
          entity: currentSectionStorage.parentEntityFromPath.type,
          name: await currentSectionStorage.getParentEntityName(
            currentSectionStorage.currentSection.project.name
          ),
        },
        {
          entity: EntityTypeFromPath.SewerObject,
          name: currentSectionStorage.currentSection.name,
        },
        {
          entity: EntityTypeFromPath.Inspection,
          name: getCurrentInspectionName(),
        },
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [
    currentSectionStorage,
    currentSectionStorage.currentSection,
    getCurrentInspectionName,
    history.location.pathname,
  ]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewDamagesList, ViewDamagesFromAssignedProject]}
      titleKey="DamagesPage.title"
      titleContext={{
        sectionName: currentSectionStorage.currentSection.name,
        inspectionName: getCurrentInspectionName(),
      }}
      menuItems={menuItems}
      isLoading={isPageLoading}
    >
      <ActionsSection
        warningKey={getDamagesAreLockedWarning()?.key}
        warningContext={getDamagesAreLockedWarning()?.context}
      >
        <ButtonsSection>
          <AddNewDamageButton
            disabled={isCreateDamageDisabled}
            currentInspectionStorage={currentInspectionStorage}
          />
          <DeleteButton
            disabled={isDeleteDamageDisabled()}
            confirmationAction={deleteSelectedDamages}
            confirmationModalTitleKey="DamagesPage.delete.confirmation.title"
            confirmationModalTitleContext={{ damagesCount: damagesStorage?.selectedItemsCount! }}
            confirmationModalSubtitleKey="DamagesPage.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
        </ButtonsSection>
      </ActionsSection>
      {damagesStorage && (
        showPipeDiagram ? (
          <Grid container direction="row" spacing={0} sx={{ position: "relative", overflow: "hidden", flexWrap: "nowrap" }}>
            <Grid item sm="auto" sx={{ position: "sticky", left: 0, zIndex: 1 }}>
              <PipeDiagram
                damages={damagesStorage.data?.items || []}
                endX={147}
              />
            </Grid>
            <Grid item sm={11}>
              <DataGrid itemTypeKey="general.damages" dataGridStorage={damagesStorage} />
            </Grid>
          </Grid>
        ) : (
          <DataGrid itemTypeKey="general.damages" dataGridStorage={damagesStorage} />
        )
      )}
    </PageLayout>
  );
});

export default DamagesPage;
